import { PosListItemDTO, useGetSuppliersOfAudiologistSuspense } from "@bvhi/backend-api";
import { Chip, GridColDef } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../components/datagrid/cells/action-cells";
import { DateCell } from "../../../../../components/datagrid/cells/date-cell";
import { useUsersSortParams } from "../../../user/users/list/hooks/use-users-sort-params";
import { PosDetailLinkCell } from "../components/datagrid/pos-detail-link-cell";
import { useAuthContext } from "../../../../../context/use-auth-context";

export const usePosDataGridDefinitions = (mode: "full" | "compact", handleDelete: (posId: string) => void) => {
  const { t } = useTranslation(["pos"]);
  const { isFieldSortable } = useUsersSortParams();

  const { currentUser } = useAuthContext();
  const { data: getSuppliersOfAudiologistResponse } = useGetSuppliersOfAudiologistSuspense(currentUser?.organisation?.id ?? "");

  const expectedSupplierCount = useMemo(() => {
    return getSuppliersOfAudiologistResponse?.data?.length ?? 0;
  }, [getSuppliersOfAudiologistResponse]);

  const columns: GridColDef<PosListItemDTO>[] = useMemo(() => {
    if (mode === "compact") {
      return [
        {
          field: "name",
          headerName: t("pos:pos.datagrid.header.name"),
          minWidth: 200,
          width: 300,
          maxWidth: 400,
          sortable: isFieldSortable("name"),
          renderCell: (params) => <PosDetailLinkCell label={params.row.name} posId={params.row.id ? params.row.id : "-1"} />,
        },
        {
          field: "connectedCustomerNumbers",
          headerName: t("pos:pos.datagrid.header.connected.customer.numbers"),
          flex: 1,
          sortable: isFieldSortable("connectedCustomerNumbers"),
          renderCell: (params) => {
            const isWarning = params.row.connectedCustomerNumbers !== expectedSupplierCount;
            return (
              <Chip
                color={isWarning ? "warning" : "success"}
                label={`${params.row.connectedCustomerNumbers} / ${expectedSupplierCount}`}
                size="small"
              />
            );
          },
        },
      ];
    }

    return [
      {
        field: "postcode",
        headerName: t("pos:pos.datagrid.header.postalCode"),
        minWidth: 150,
        width: 150,
        maxWidth: 250,
        sortable: isFieldSortable("postcode"),
        renderCell: (params) => <PosDetailLinkCell label={params.row.postcode} posId={params.row.id ? params.row.id : "-1"} />,
      },
      {
        field: "name",
        headerName: t("pos:pos.datagrid.header.name"),
        minWidth: 200,
        width: 250,
        maxWidth: 300,
        sortable: isFieldSortable("name"),
      },
      {
        field: "connectedCustomerNumbers",
        headerName: t("pos:pos.datagrid.header.connected.customer.numbers"),
        minWidth: 150,
        width: 200,
        maxWidth: 300,
        sortable: isFieldSortable("connectedCustomerNumbers"),
        renderCell: (params) => {
          const isWarning = params.row.connectedCustomerNumbers !== expectedSupplierCount;
          return (
            <Chip
              color={isWarning ? "warning" : "primary"}
              label={`${params.row.connectedCustomerNumbers} / ${expectedSupplierCount}`}
              size="small"
            />
          );
        },
      },
      {
        field: "createdAt",
        headerName: t("pos:pos.datagrid.header.createdAt"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("createdAt"),
        renderCell: (params) => <DateCell date={params.row.createdAt} />,
      },
      {
        field: "updatedAt",
        headerName: t("pos:pos.datagrid.header.updatedAt"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("updatedAt"),
        renderCell: (params) => <DateCell date={params.row.updatedAt} />,
      },
      {
        field: "id",
        headerName: t("pos:pos.datagrid.header.actions"),
        flex: 1,
        minWidth: 100,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: isFieldSortable("id"),
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deletePermission="pos:delete"
            detailParams={{ posId: params.row.id }}
            detailPermission="pos:read"
            detailRoute="/app/pos/$posId"
            handleDelete={handleDelete}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
          />
        ),
      },
    ];
  }, [mode, t, isFieldSortable, expectedSupplierCount, handleDelete]);

  return { columns };
};
